import { Accordion, ACCORDION_VARIATION } from '@cox/core-ui8/dist/Accordion'
import { ButtonTypes, LinkTypes } from '@cox/core-ui8/dist/Button'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'
import parse from 'html-react-parser'
import { renderToString } from 'react-dom/server'

import ReactPSUTeaserSkeleton from './ReactPSUTeaserSkeleton'
import { SM_CHEVRON_DOWN_RIVER_BLUE } from '../../../assets/images'
import { DynamicMediaComponents, getDynamicMediaFromSrc } from '../../../utils/dynamic-media-util'
import { groupBy } from '../../../utils/helper-util'
import { getAttributeValue, hasCFConditionals } from '../../../utils/rule-builder-util'
import ReactBadge from '../../ReactBadge'
import ReactButton from '../../ReactButton'
import ReactPrice, { PriceVariant } from '../../ReactPrice'
import ReactText from '../../ReactText'
import { ReactPSUTeaserProps } from '../types'
import './ReactPSUTeaser.scss'

const ReactPSUTeaser = (props: ReactPSUTeaserProps): JSX.Element => {
  const { id, elements, ruleExpression = '', tokenProperties, priceTagIcon, index, badgeRef, badgeHeight } = props
  const flexOffers = useFlexOffers(ruleExpression)
  const {
    accordionItems,
    additionalNote,
    altImage,
    bulletIcon,
    bulletPoints,
    contentBadgeColor,
    contentBadgeText,
    description,
    discount,
    eyebrow,
    eyebrowIcon,
    eyebrowIconAltText,
    heading,
    image,
    newBillText,
    planName,
    price,
    priceChangeText,
    psuItems,
    scratchPrice,
    term,
    title1,
    /* PRIMARY BUTTON */
    buttonTextPrimary = { value: '' },
    buttonLinkPrimary = { value: '' },
    buttonLinkTypePrimary = { value: '' },
    buttonLinkNewTabPrimary = { value: '' },
    linkTypeButtonPrimary = { value: '' },
    linkIdButtonPrimary = { value: '' },
    /* SECONDARY BUTTON */
    buttonTextSecondary = { value: '' },
    buttonLinkSecondary = { value: '' },
    buttonLinkNewTabSecondary = { value: '' },
    buttonLinkTypeSecondary = { value: '' },
    linkTypeButtonSecondary = { value: '' },
    linkIdButtonSecondary = { value: '' },
    /* TERTIARY BUTTON */
    buttonTextTertiary = { value: '' },
    buttonLinkTertiary = { value: '' },
    buttonLinkNewTabTertiary = { value: '' },
    buttonLinkTypeTertiary = { value: '' },
    linkTypeButtonTertiary = { value: '' },
    linkIdButtonTertiary = { value: '' }
  } = elements

  const getValue = (attribute: any, propertyName?: string) => {
    if (hasCFConditionals(attribute)) {
      const jsonAttribute = (elements as any)?.[`${propertyName}_json`]
      return getAttributeValue(jsonAttribute, flexOffers, index, tokenProperties, propertyName)
    }
    return getAttributeValue(attribute, flexOffers, index, tokenProperties, propertyName)
  }

  if (flexOffers?.loading) {
    return <ReactPSUTeaserSkeleton />
  }

  const additionalNoteValue = getValue(additionalNote, 'additionalNote')
  const altImageValue = getValue(altImage, 'altImage')
  const bulletIconValue = getValue(bulletIcon, 'bulletIcon')
  const contentBadgeColorValue = getValue(contentBadgeColor, 'contentBadgeColor')
  const contentBadgeTextValue = getValue(contentBadgeText, 'contentBadgeText')
  const descriptionValue = getValue(description, 'description')
  const discountValue = getValue(discount, 'discount')
  const eyebrowIconAltTextValue = getValue(eyebrowIconAltText, 'eyebrowIconAltText')
  const eyebrowIconValue = getDynamicMediaFromSrc(
    getValue(eyebrowIcon, 'eyebrowIcon') as string,
    DynamicMediaComponents.PSU_TEASER
  )
  const eyebrowValue = getValue(eyebrow, 'eyebrow')
  const headingValue = getValue(heading, 'heading')
  const imageValue = getDynamicMediaFromSrc(getValue(image, 'image') as string, DynamicMediaComponents.PSU_TEASER)
  const newBillTextValue = getValue(newBillText, 'newBillText')
  const planNameValue = getValue(planName, 'planName')
  const priceChangeTextValue = getValue(priceChangeText, 'priceChangeText')
  const priceValue = getValue(price, 'price')
  const scratchPriceValue = getValue(scratchPrice, 'scratchPrice')
  const termValue = getValue(term, 'term')

  /* PRIMARY BUTTON */
  const primaryButtonType = buttonLinkTypePrimary?.value || ButtonTypes.PRIMARY
  const buttonTextPrimaryValue = getValue(buttonTextPrimary, 'buttonTextPrimary')
  const buttonLinkPrimaryValue = getValue(buttonLinkPrimary, 'buttonLinkPrimary')
  const buttonLinkNewTabPrimaryValue = getValue(buttonLinkNewTabPrimary, 'buttonLinkNewTabPrimary')
  const linkIdButtonPrimaryValue = getValue(linkIdButtonPrimary, 'linkIdButtonPrimary')
  const linkTypeButtonPrimaryValue = linkTypeButtonPrimary?.value

  /* SECONDARY BUTTON */
  const secondaryButtonType = buttonLinkTypeSecondary?.value || ButtonTypes.SECONDARY
  const buttonTextSecondaryValue = getValue(buttonTextSecondary, 'buttonTextSecondary')
  const buttonLinkSecondaryValue = getValue(buttonLinkSecondary, 'buttonLinkSecondary')
  const buttonLinkNewTabSecondaryValue = getValue(buttonLinkNewTabSecondary, 'buttonLinkNewTabSecondary')
  const linkIdButtonSecondaryValue = getValue(linkIdButtonSecondary, 'linkIdButtonSecondary')
  const linkTypeButtonSecondaryValue = linkTypeButtonSecondary?.value

  /* TERTIARY BUTTON */
  const tertiaryButtonType = buttonLinkTypeTertiary?.value || ButtonTypes.TERTIARY
  const buttonTextTertiaryValue = getValue(buttonTextTertiary, 'buttonTextTertiary')
  const buttonLinkTertiaryValue = getValue(buttonLinkTertiary, 'buttonLinkTertiary')
  const buttonLinkNewTabTertiaryValue = getValue(buttonLinkNewTabTertiary, 'buttonLinkNewTabTertiary')
  const linkIdButtonTertiaryValue = getValue(linkIdButtonTertiary, 'linkIdButtonTertiary')
  const linkTypeButtonTertiaryValue = linkTypeButtonTertiary?.value

  /* Accordion Items */
  let accordionItemsValue = []
  if (accordionItems) {
    // If accordion is static or dynamic, then accordionItems are passed and processed as follows:
    for (let i = 0; i < accordionItems?.value.length; i++) {
      const accordionItem = JSON.parse(accordionItems.value[i])

      const title1Value = getValue(accordionItem.title1, 'title1')
      const title2Value = getValue(accordionItem.title2, 'title2')
      const accordionIconValue = getValue(accordionItem.accordionIcon, 'accordionIcon')
      const accordionIconAlttxtValue = getValue(accordionItem.accordionIconAlttxt, 'accordionIconAlttxt')
      const bulletPointsValue = getValue(accordionItem.bulletPoints, 'bulletPoints')

      accordionItem.title1 = title1Value
      accordionItem.title2 = title2Value
      accordionItem.accordionIcon = accordionIconValue
      accordionItem.accordionIconAlttxt = accordionIconAlttxtValue
      const filteredBulletPointsValue = bulletPointsValue
        .toString()
        .split('\n')
        .filter((item: string) => item.length > 1)
      accordionItem.bulletPoints = filteredBulletPointsValue
      accordionItem.bulletIcon = bulletIconValue

      if (accordionItem.title1 && accordionItem.bulletPoints.length > 0) {
        accordionItemsValue.push(accordionItem)
      }
    }
  } else if (psuItems && title1 && bulletPoints) {
    // If conditional accordion, then psuItems, title1, & bulletPoints props are passed and processed as follows:
    const psuItemsValue = psuItems?.value
    const title1Value = title1?.value
    const bulletPointsValue = bulletPoints?.value

    /**** BASELINE ACCORDION ITEM ****/

    // To get baseline accordionItems based on the psuItems passed:
    for (const element of psuItemsValue) {
      const accordionItem = JSON.parse(element)

      const productNameValue = getValue(accordionItem.productName, 'productName')
      const title2Value = getValue(accordionItem.title2, 'title2')

      accordionItem.productName = productNameValue
      accordionItem.title2 = title2Value

      accordionItemsValue.push(accordionItem)
    }

    /**** TITLE1 ****/

    // Get title1 rule expressions by product name:
    const title1RuleExpressions = []
    for (const element of title1Value) {
      const title1Item = JSON.parse(element)
      title1RuleExpressions.push(title1Item)
    }
    const groupedByProductName = groupBy(title1RuleExpressions, (product: any) => product.productName)

    // Get unique productNames (these correspond to unique accordionItems):
    const productItems = Object.keys(groupedByProductName)

    // Get title1 values evaluated by productName:
    for (const productItem of productItems) {
      const title1Item = getAttributeValue(
        { value: groupedByProductName[productItem], dataType: 'string' },
        flexOffers,
        index,
        tokenProperties,
        'title1',
        false
      )

      const accordionIconItem = getAttributeValue(
        { value: groupedByProductName[productItem], dataType: 'string' },
        flexOffers,
        index,
        tokenProperties,
        'accordionIcon',
        false,
        'accordionIcon'
      )

      const accordionIconAlttxtItem = getAttributeValue(
        { value: groupedByProductName[productItem], dataType: 'string' },
        flexOffers,
        index,
        tokenProperties,
        'accordionIconAlttxt',
        false,
        'accordionIconAlttxt'
      )

      for (const accordionItem of accordionItemsValue) {
        if (accordionItem.productName == productItem) {
          accordionItem.title1 = title1Item
          accordionItem.accordionIcon = accordionIconItem
          accordionItem.accordionIconAlttxt = accordionIconAlttxtItem
        }
      }
    }

    /**** BULLET POINTS ****/

    // Get bullet point rule expressions by product name:
    const bulletPointsRuleExpressions = []
    for (const element of bulletPointsValue) {
      const bulletPointItem = JSON.parse(element)
      bulletPointsRuleExpressions.push(bulletPointItem)
    }
    const groupedBulletsByProductName = groupBy(bulletPointsRuleExpressions, (product: any) => product.productName)

    // Get bullet point values evaluated by productName:
    for (const productItem of productItems) {
      const bulletItems = getAttributeValue(
        { value: groupedBulletsByProductName[productItem], dataType: 'string' },
        flexOffers,
        index,
        tokenProperties,
        'bulletPoints'
      )

      for (const accordionItem of accordionItemsValue) {
        if (accordionItem.productName == productItem) {
          const filteredBulletPointsValue = renderToString(bulletItems)
            .split('\n')
            .filter((item: string) => item.length > 1)

          // Convert filteredBulletPointsValue object to array and parse each bullet point to render HTML:
          const filteredBulletPointsArray = Object.entries(filteredBulletPointsValue)
          const bulletPointsArray = []
          for (let i = 0; i < filteredBulletPointsArray.length; i++) {
            bulletPointsArray.push(parse(filteredBulletPointsArray[i][1]))
          }

          accordionItem.bulletPoints = bulletPointsArray
        }
      }
    }

    // Filters out any accordions without a title1 or any bullet points:
    accordionItemsValue = accordionItemsValue.filter(
      (accordionItem) => accordionItem?.title1?.length > 1 && accordionItem.bulletPoints.length > 0
    )
  }

  return (
    <div data-testid='react-psu-teaser-card' className='react-psu-teaser-card' id={id}>
      {/* Top */}
      <div className='top-section'>
        {/* Badge */}
        <div ref={(ref: any) => ((badgeRef.current[index as any] as any) = ref)} style={{ minHeight: `${badgeHeight}px` }}>
          {contentBadgeTextValue && (
            <ReactBadge
              contentBadgeText={contentBadgeTextValue}
              contentBadgeColor={contentBadgeColorValue}
              isInContainer={true}
            />
          )}
        </div>
        {/* Image */}
        {imageValue && (
          <div className='icon'>
            <img src={imageValue} alt={altImageValue} title={altImageValue}></img>
          </div>
        )}
        {/* Eyebrow with Image */}
        {eyebrowValue && (
          <div className='item-eyebrow'>
            {eyebrowIconValue && <img src={eyebrowIconValue} alt={eyebrowIconAltTextValue} className='eyebrow-icon' />}
            <ReactText text={eyebrowValue} isParsed />
          </div>
        )}
        {/* Header */}
        {headingValue && <ReactText text={headingValue} isParsed className='header' />}
        {/* Additional Note */}
        {additionalNoteValue && <ReactText text={additionalNoteValue} isParsed className='additional-note' />}
        {/* Description */}
        {descriptionValue && <ReactText text={descriptionValue} isParsed className='heading-description' />}
        {/* Accordion */}
        <Accordion
          accordionIcon={SM_CHEVRON_DOWN_RIVER_BLUE}
          variation={ACCORDION_VARIATION.PSU}
          accordionItems={accordionItemsValue}
          defaultActiveKey={'accordion-item-1'}
        />
      </div>

      {/* Bottom */}
      <div className='bottom-section'>
        {/* Price Section */}
        <ReactPrice
          variant={PriceVariant.PSU}
          planName={planNameValue}
          priceChangeText={priceChangeTextValue}
          price={priceValue}
          scratchPrice={scratchPriceValue}
          newBillText={newBillTextValue}
          discount={discountValue}
          priceTagIcon={priceTagIcon}
          term={termValue}
        />
        {/* Buttons */}
        <div className='button-section'>
          {buttonTextPrimaryValue.length > 0 && (
            <ReactButton
              text={buttonTextPrimaryValue}
              buttonTypes={primaryButtonType as ButtonTypes}
              linkType={linkTypeButtonPrimaryValue as LinkTypes}
              linkId={linkIdButtonPrimaryValue}
              linkUrl={buttonLinkPrimaryValue}
              openInNewTab={buttonLinkNewTabPrimaryValue}
              index={index}
              ruleExpression={ruleExpression}
            />
          )}
          {buttonTextSecondaryValue.length > 0 && (
            <ReactButton
              text={buttonTextSecondaryValue}
              buttonTypes={secondaryButtonType as ButtonTypes}
              linkType={linkTypeButtonSecondaryValue as LinkTypes}
              linkId={linkIdButtonSecondaryValue}
              linkUrl={buttonLinkSecondaryValue}
              openInNewTab={buttonLinkNewTabSecondaryValue}
              index={index}
              ruleExpression={ruleExpression}
            />
          )}
          {buttonTextTertiaryValue.length > 0 && (
            <ReactButton
              text={buttonTextTertiaryValue}
              buttonTypes={tertiaryButtonType as ButtonTypes}
              linkType={linkTypeButtonTertiaryValue as LinkTypes}
              linkId={linkIdButtonTertiaryValue}
              linkUrl={buttonLinkTertiaryValue}
              openInNewTab={buttonLinkNewTabTertiaryValue}
              index={index}
              ruleExpression={ruleExpression}
            />
          )}
        </div>
      </div>
    </div>
  )
}
export default ReactPSUTeaser
